import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tradeLicenseApi } from '../../../services/tradeLicenseApi';
import ListPagination from '../../../components/Office/ListPagination';

const PAGE_SIZE = 20;

const PendingApprovalsList = () => {
  const [approvalTab, setApprovalTab] = useState('pending');
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tradeLicenseApi.getPendingApprovals({
        scope: approvalTab,
        page,
        page_size: PAGE_SIZE,
      });
      const data = response.data;
      setApplications(data.results || data);
      setPagination({
        count: data.count ?? (Array.isArray(data) ? data.length : 0),
        next: data.next ?? null,
        previous: data.previous ?? null,
      });
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
      setApplications([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      setLoading(false);
    }
  }, [approvalTab, page]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleTabChange = (tab) => {
    setApprovalTab(tab);
    setPage(1);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1 className="page-title" style={{ margin: 0 }}>Pending Approvals</h1>
      </div>

      <p style={{ color: '#666', marginBottom: 16 }}>
        Applications waiting for your action, and applications you have already approved in the workflow.
      </p>

      <div style={{ marginBottom: 20, display: 'flex', gap: 10 }}>
        <button
          type="button"
          className={`btn ${approvalTab === 'pending' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => handleTabChange('pending')}
        >
          Pending For Me
        </button>
        <button
          type="button"
          className={`btn ${approvalTab === 'approved' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => handleTabChange('approved')}
        >
          Approved By Me
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <>
          <div className="card">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #ddd' }}>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Application #</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Business Name</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Applicant</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Assigned To</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Current Stage</th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                  {approvalTab === 'approved' && (
                    <th style={{ padding: '12px', textAlign: 'left' }}>My Action</th>
                  )}
                  <th style={{ padding: '12px', textAlign: 'left' }}>
                    {approvalTab === 'approved' ? 'Action Date' : 'Submitted'}
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left' }}>View</th>
                </tr>
              </thead>
              <tbody>
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <tr key={app.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '12px' }}>
                        <strong>{app.application_number}</strong>
                      </td>
                      <td style={{ padding: '12px' }}>{app.business_name_en}</td>
                      <td style={{ padding: '12px' }}>{app.applicant_email || app.applicant_name}</td>
                      <td style={{ padding: '12px' }}>{app.assigned_to_name || app.assigned_to_username || '—'}</td>
                      <td style={{ padding: '12px' }}>{app.current_stage_name || '—'}</td>
                      <td style={{ padding: '12px' }}>
                        <span className={`status-badge ${app.status}`}>{app.status}</span>
                      </td>
                      {approvalTab === 'approved' && (
                        <td style={{ padding: '12px' }}>
                          {(app.my_last_action || 'approved').replace(/_/g, ' ')}
                        </td>
                      )}
                      <td style={{ padding: '12px' }}>
                        {approvalTab === 'approved' && app.my_last_action_at
                          ? new Date(app.my_last_action_at).toLocaleString()
                          : app.submitted_date
                            ? new Date(app.submitted_date).toLocaleDateString()
                            : new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <Link to={`/office/applications/${app.id}`} className="btn btn-primary">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={approvalTab === 'approved' ? 9 : 8} style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                      {approvalTab === 'pending'
                        ? 'No pending approvals assigned to you'
                        : 'No applications approved by you yet'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <ListPagination
            count={pagination.count}
            next={pagination.next}
            previous={pagination.previous}
            currentPage={page}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default PendingApprovalsList;
